/**
 * DeviceContentCard — DE-171 content visibility (plan 2E) + device-truth drill-down.
 *
 * Shows what content the device is actually running (applied = the telemetry
 * `content` shadow category, per-collection counts) next to what the Portal has
 * assigned it. A factory/never-synced device reports profile "factory" per
 * collection and has no assignment — the day-one baseline, not an error.
 *
 * The match badge mirrors the Mender deployment pill grammar (OpsDeviceGroups).
 * "Assigned" resolves device-override-beats-group (the `via` line says which).
 *
 * DRILL-DOWN: clicking a collection row expands the LITERAL files on the device
 * (device_content_inventory, uplinked by telemetry) — name, size, type,
 * duration, sha — fetched lazily (only when expanded), with drift markers
 * ("extra" = on device but not in the assigned profile; a "missing" line for the
 * reverse).
 */
import { Fragment, useState } from 'react';
import {
  useDeviceContent,
  useDeviceContentFiles,
  ENV_GROUP_LABEL,
  type EnvGroup,
} from '../api/hooks';
import { contentSyncStatus, CONTENT_BADGE } from './contentSync';
import { fmtAgo, fmtBytes, fmtMSS, KIND_GLYPH, shortSha } from './format';
import { MediaPreviewModal } from './MediaPreviewModal';

function dash(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

export function DeviceContentCard({ thing, envGroup }: { thing: string; envGroup?: string | null }) {
  // Pass the device's env-group so the backend resolves the effective assigned
  // profile from the release ledger: device-target release wins, else the
  // group's release (the `via` field on `assigned` says which).
  const content = useDeviceContent(thing, envGroup);
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ assetId: string; kind: string; title: string; posterUrl?: string } | null>(null);
  // Lazy: only fetches once a collection is expanded. One request serves all
  // collections (sliced client-side); doesn't ride the 15 s useDeviceContent poll.
  const files = useDeviceContentFiles(thing, envGroup, openCol !== null);

  const data = content.data;
  const applied = data?.applied ?? null;
  // Backend-resolved effective assignment (override beats group); `via` says which.
  const assigned = data?.assigned ?? null;
  const collections = applied?.collections ?? {};
  const colNames = Object.keys(collections).sort();

  // Badge: does the assigned profile version show up in the reported content?
  const badge = CONTENT_BADGE[contentSyncStatus(applied, assigned)];

  const viaLabel =
    assigned?.via === 'group'
      ? `via group ${ENV_GROUP_LABEL[(envGroup ?? '') as EnvGroup] ?? envGroup}`
      : 'device override';

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-medium">Content</h2>
        {content.data && <span className={badge.cls}>{badge.label}</span>}
      </div>

      {content.isLoading && <p className="text-sm text-muted">Loading…</p>}
      {content.error && (
        <p className="text-sm text-warn">{(content.error as Error).message}</p>
      )}

      {content.data && (
        <div className="space-y-4">
          {/* Assigned (desired) */}
          <div className="text-sm">
            <span className="text-muted">Assigned profile: </span>
            {!assigned ? (
              <span className="text-muted">none (shipped factory content)</span>
            ) : assigned.profile_version === 'factory' ? (
              <span className="text-muted">factory (shipped baseline)</span>
            ) : (
              <span className="font-medium">
                {assigned.profile_name}{' '}
                <span className="font-mono text-xs text-muted">v{assigned.profile_version}</span>
                <span className="text-muted"> · {viaLabel} · {fmtAgo(assigned.resolved_at)}</span>
              </span>
            )}
          </div>

          {/* Applied (reported) per-collection — click a row to drill into files */}
          {colNames.length === 0 ? (
            <p className="text-sm text-muted">No content reported yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  <th className="font-medium py-1.5 w-5" />
                  <th className="font-medium py-1.5 pr-4">Collection</th>
                  <th className="font-medium py-1.5 pr-4">Profile</th>
                  <th className="font-medium py-1.5 pr-4">Version</th>
                  <th className="font-medium py-1.5 pr-4">Files</th>
                  <th className="font-medium py-1.5">Layout</th>
                </tr>
              </thead>
              <tbody>
                {colNames.map((n) => {
                  const c = collections[n] ?? {};
                  const open = openCol === n;
                  return (
                    <Fragment key={n}>
                      <tr
                        onClick={() => setOpenCol(open ? null : n)}
                        className="border-b border-border last:border-0 cursor-pointer hover:bg-soft/60"
                        title="Show files on the device"
                      >
                        <td className="py-1.5 pr-1 text-muted text-xs">{open ? '▼' : '▶'}</td>
                        <td className="py-1.5 pr-4 font-mono text-xs">{n}</td>
                        <td className="py-1.5 pr-4">{dash(c.profile)}</td>
                        <td className="py-1.5 pr-4 font-mono text-xs">{c.version ? `v${c.version}` : '—'}</td>
                        <td className="py-1.5 pr-4">{dash(c.fileCount)}</td>
                        <td className="py-1.5">
                          <span className={c.managed ? 'pill-accent' : 'pill-muted'}>
                            {c.managed ? 'managed' : 'factory'}
                          </span>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            {/* key per collection → per-item expand state resets on collection switch */}
                            <CollectionFilesPanel key={n} q={files} collection={n} onPlay={setPreview} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {preview && (
        <MediaPreviewModal
          assetId={preview.assetId}
          kind={preview.kind}
          title={preview.title}
          posterUrl={preview.posterUrl}
          onClose={() => setPreview(null)}
        />
      )}
    </section>
  );
}

// Grouped device-truth files: one row per logical item (testimonial =
// video+poster+text, song = audio+cover), expandable to its literal parts.
function CollectionFilesPanel({
  q,
  collection,
  onPlay,
}: {
  q: ReturnType<typeof useDeviceContentFiles>;
  collection: string;
  onPlay: (p: { assetId: string; kind: string; title: string; posterUrl?: string }) => void;
}) {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (q.isLoading) {
    return <div className="px-3 py-3"><div className="h-10 bg-soft rounded animate-pulse" /></div>;
  }
  if (q.error) {
    return <div className="px-3 py-3 text-xs text-warn">{(q.error as Error).message}</div>;
  }
  const data = q.data;
  if (!data || data.reportedAt === null) {
    return (
      <div className="px-3 py-3 text-xs text-muted">
        This device hasn’t reported its file inventory yet (needs the updated telemetry build —
        appears after its next report).
      </div>
    );
  }
  const col = data.collections.find((c) => c.collection === collection);
  if (!col || col.items.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-muted">
        No files reported on the device for this collection.
      </div>
    );
  }
  return (
    <div className="px-3 py-3 bg-soft/40 space-y-2">
      <div className="text-[11px] text-muted">
        {col.items.length} item{col.items.length === 1 ? '' : 's'} · {col.files.length} file
        {col.files.length === 1 ? '' : 's'} on device · reported {fmtAgo(data.reportedAt)}
      </div>
      <table className="w-full text-xs">
        <tbody>
          {col.items.map((it) => {
            const expandable = it.files.length > 1;
            const open = openItems.has(it.itemId);
            const showParts = it.partsPresent.length + it.partsMissing.length > 1;
            // primary playable file (files are primary-first sorted server-side)
            const primaryPlayable = it.files.find(
              (f) => f.assetId && (f.kind === 'video' || f.kind === 'audio'));
            return (
              <Fragment key={it.itemId}>
                <tr
                  className={`border-b border-border/60 last:border-0 ${expandable ? 'cursor-pointer hover:bg-soft/60' : ''}`}
                  onClick={expandable ? () => toggle(it.itemId) : undefined}
                  onKeyDown={
                    expandable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(it.itemId);
                          }
                        }
                      : undefined
                  }
                  role={expandable ? 'button' : undefined}
                  tabIndex={expandable ? 0 : undefined}
                  aria-expanded={expandable ? open : undefined}
                  aria-label={expandable ? `Show files for ${it.title}` : undefined}
                >
                  <td className="py-1 pr-1 text-muted text-[10px] w-3">{expandable ? (open ? '▾' : '▸') : ''}</td>
                  <td className="py-1 pr-2 text-muted" title={it.kind ?? ''}>{KIND_GLYPH[it.kind ?? ''] ?? '≡'}</td>
                  <td className="py-1 pr-3">
                    {primaryPlayable && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlay({ assetId: primaryPlayable.assetId!, kind: primaryPlayable.kind!, title: it.title, posterUrl: it.thumbnailUrl ?? undefined });
                        }}
                        aria-label={`Play ${it.title}`}
                        title="Preview"
                        className="mr-1.5 align-middle text-accent hover:text-accent/80"
                      >
                        ▶
                      </button>
                    )}
                    <span className="align-middle">{it.title}</span>
                    {showParts && (
                      <span className="ml-2 inline-flex gap-0.5 align-middle">
                        {it.partsPresent.map((k) => (
                          <span key={k} title={k} className="text-muted">{KIND_GLYPH[k] ?? '≡'}</span>
                        ))}
                        {it.partsMissing.map((k) => (
                          <span key={`m-${k}`} title={`missing ${k}`} aria-label={`missing ${k}`} className="opacity-30">
                            {KIND_GLYPH[k] ?? '≡'}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="py-1 pr-2 whitespace-nowrap">
                    {!it.completeOnDevice && (
                      <span className="pill-warn" title={`Missing on device: ${it.partsMissing.join(', ')}`}>incomplete</span>
                    )}
                    {it.anyExtra && (
                      <span className="pill-warn ml-1" title="Has a file not in the assigned profile">extra</span>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-right whitespace-nowrap">{fmtBytes(it.totalBytes)}</td>
                  <td className="py-1 text-right text-muted whitespace-nowrap">{fmtMSS(it.duration_s)}</td>
                </tr>
                {expandable && open && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <table className="w-full pl-6">
                        <tbody>
                          {it.files.map((f) => {
                            const partPlayable = f.assetId && (f.kind === 'video' || f.kind === 'audio');
                            return (
                            <tr key={f.name} className="border-b border-border/40 last:border-0">
                              <td className="py-0.5 pl-6 pr-2 text-muted">
                                {partPlayable ? (
                                  <button
                                    type="button"
                                    onClick={() => onPlay({ assetId: f.assetId!, kind: f.kind!, title: f.title ?? f.name, posterUrl: it.thumbnailUrl ?? undefined })}
                                    aria-label={`Play ${f.name}`}
                                    title="Preview"
                                    className="text-accent hover:text-accent/80"
                                  >
                                    ▶
                                  </button>
                                ) : (
                                  <span title={f.kind ?? ''}>{KIND_GLYPH[f.kind ?? ''] ?? '≡'}</span>
                                )}
                              </td>
                              <td className="py-0.5 pr-3 font-mono text-[10px] text-muted truncate max-w-[220px]" title={f.name}>
                                {f.name}
                              </td>
                              <td className="py-0.5 pr-3 text-right whitespace-nowrap">{fmtBytes(f.bytes)}</td>
                              <td className="py-0.5 pr-2 font-mono text-[10px] text-muted" title={f.sha256 ?? ''}>{shortSha(f.sha256)}</td>
                              <td className="py-0.5 text-right">
                                {f.inProfile === false && (
                                  <span className="pill-warn" title="On the device but not in the assigned profile">extra</span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {col.missingFromDevice.length > 0 && (
        <div className="text-[11px] text-warn">
          ⚠ In the assigned profile but not on the device: {col.missingFromDevice.join(', ')}
        </div>
      )}
    </div>
  );
}
